import { Pagination, Role, UserRoleNodeMapping } from '@ekkogmbh/apisdk';
import { Add, ArrowBack, Delete, EditCalendarOutlined } from '@mui/icons-material';
import { Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { orange, red } from '@mui/material/colors';
import moment, { Moment } from 'moment';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminTable, AdminTableColumn } from 'src/Common/Components/AdminTable';
import { RowActionsMenu } from 'src/Common/Components/RowActionsMenu';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { LoadingMask } from 'src/Common/Components/LoadingMask';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import { UserAvatar } from 'src/Common/Components/UserAvatar';
import { injectFakePagination } from 'src/Common/Helper/Pagination';
import useRequest from 'src/Common/Hooks/useRequest';
import Permissions from 'src/Common/Permissions';
import useEkko from 'src/Common/Providers/EkkoProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { SettingsSection } from 'src/SettingsSection/Components/SettingsSection';
import { UserMappingDrawer } from 'src/UserManagement/Components/UserMappingDrawer';
import { useSettingsSection } from '../Providers/SettingsSectionProvider';

type DialogKind = 'delete' | 'valid' | undefined;

export const RoleMembersAdminSection = (): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const { setDrawerOpen } = useSettingsSection();

  const navigate = useNavigate();
  const { roleId } = useParams();
  const { refresh: triggerRefresh } = usePagination();

  const [isFetchingRole, startFetchingRole] = useRequest<Role>();
  const [role, setRole] = useState<Role | undefined>();
  const [dialog, setDialog] = useState<DialogKind>();
  const [selectedMapping, setSelectedMapping] = useState<UserRoleNodeMapping | undefined>();

  const canWrite = useMemo(
    () => hasPermission(Permissions.MAPPINGS_WRITE) || hasPermission(Permissions.MAPPINGS_WRITE_RESTRICTED),
    [hasPermission],
  );

  const dismiss = useCallback(() => {
    setDrawerOpen(false);
    setDialog(undefined);
    setSelectedMapping(undefined);
  }, []);

  useEffect(() => {
    if (!roleId) {
      return;
    }
    startFetchingRole(
      () => api.getRole(Number(roleId)),
      (result) => setRole(result as Role),
    );
  }, [api, roleId, startFetchingRole]);

  const fetchItems = useCallback(
    async (pagination: Pagination) => {
      const mappings =
        role === undefined ? [] : await api.getUserRoleNodeMappings(undefined, role, undefined, true, true);

      return injectFakePagination<UserRoleNodeMapping>(mappings, pagination);
    },
    [api, role],
  );

  const openCreate = useCallback(() => {
    setSelectedMapping(undefined);
    setDrawerOpen(true);
  }, []);

  const onDeleteConfirm = useCallback(async () => {
    if (!selectedMapping?.id) {
      return;
    }
    await api.deleteUserRoleNodeMapping({ id: selectedMapping.id });
    enqueueSnackbar('Mapping deleted.');
    dismiss();
    triggerRefresh();
  }, [api, selectedMapping, triggerRefresh]);

  const onValidUntilConfirm = useCallback(async () => {
    if (!selectedMapping?.id || !selectedMapping.validUntil) {
      return;
    }
    await api.updateUserRoleNodeMapping({
      id: selectedMapping.id,
      validUntil: selectedMapping.validUntil,
    });
    enqueueSnackbar('Valid-until updated.');
    dismiss();
    triggerRefresh();
  }, [api, selectedMapping, triggerRefresh]);

  const onChangeValidUntil = useCallback(
    (date: Moment | null) => {
      if (!selectedMapping || date === null) {
        return;
      }
      setSelectedMapping({ ...selectedMapping, validUntil: date.format('YYYY-MM-DD') });
    },
    [selectedMapping],
  );

  const columns = useMemo<AdminTableColumn<UserRoleNodeMapping>[]>(
    () => [
      {
        field: 'user',
        headerName: 'User',
        size: 220,
        sortable: false,
        filterable: false,
        renderCell: (row) => (
          <Stack direction={'row'} alignItems={'center'} spacing={1.5} sx={{ width: '100%' }}>
            <UserAvatar username={row.user?.username ?? ''} size={'small'} />
            <Typography fontWeight={600} noWrap>
              {row.user?.username ?? '—'}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'coordinate',
        headerName: 'Coordinate',
        size: 300,
        sortable: false,
        filterable: false,
        renderCell: (row) => (
          <Typography variant={'body2'} color={'text.secondary'} noWrap>
            {row.node?.coordinate ?? '—'}
          </Typography>
        ),
      },
      {
        field: 'validUntil',
        headerName: 'Valid-Until',
        size: 240,
        sortable: false,
        filterable: false,
        renderCell: (row) => {
          if (row.user?.automation) {
            return (
              <Chip
                label={'No expiry'}
                size={'small'}
                sx={{
                  backgroundColor: 'action.selected',
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: 11,
                  height: 20,
                }}
              />
            );
          }
          const m = moment(row.validUntil);
          const hours = m.diff(moment(), 'hours');
          const isExpired = hours <= 0;
          const isSoon = hours > 0 && hours < 48;
          return (
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <Tooltip title={'Edit valid-until'}>
                <Typography
                  onClick={() => {
                    if (!canWrite) {
                      return;
                    }
                    setSelectedMapping(row);
                    setDialog('valid');
                  }}
                  sx={{
                    cursor: canWrite ? 'pointer' : 'default',
                    borderBottom: '1px dashed',
                    borderColor: 'text.secondary',
                    '&:hover': canWrite ? { borderColor: 'primary.main', color: 'primary.main' } : undefined,
                  }}
                >
                  {m.format('DD MMM YYYY')}
                </Typography>
              </Tooltip>
              <Chip
                label={m.fromNow()}
                size={'small'}
                sx={{
                  backgroundColor: isExpired ? red[500] : isSoon ? orange[500] : 'action.selected',
                  color: isExpired || isSoon ? 'white' : 'text.secondary',
                  fontWeight: 500,
                  fontSize: 11,
                  height: 20,
                }}
              />
            </Stack>
          );
        },
      },
      {
        field: '__actions',
        headerName: '',
        size: 60,
        sortable: false,
        filterable: false,
        resizable: false,
        hideInColumnsPopover: true,
        align: 'center',
        renderCell: (row) => (
          <RowActionsMenu<UserRoleNodeMapping>
            row={row}
            actions={[
              {
                label: 'Edit',
                icon: <EditCalendarOutlined fontSize="small" />,
                onClick: (m) => {
                  setSelectedMapping(m);
                  setDialog('valid');
                },
                disabled: (m) => !canWrite || !!m.user?.automation,
              },
              {
                label: 'Delete',
                icon: <Delete fontSize="small" />,
                onClick: (m) => {
                  setSelectedMapping(m);
                  setDialog('delete');
                },
                disabled: () => !canWrite,
              },
            ]}
          />
        ),
      },
    ],
    [canWrite],
  );

  const dialogElement = useMemo(() => {
    if (!dialog || !selectedMapping) {
      return undefined;
    }
    if (dialog === 'delete') {
      return (
        <GenericDialog
          open
          fullWidth
          centered
          timedOkButton
          type={'confirmation'}
          maxWidth={'sm'}
          title={'Delete mapping'}
          text={
            <CenteredColumn>
              <Typography>
                Remove role &ldquo;{selectedMapping.role?.name}&rdquo; from {selectedMapping.user?.username} on{' '}
                {selectedMapping.node?.coordinate}?
              </Typography>
              <Typography style={{ margin: 16 }} variant={'caption'}>
                This action can not be undone.
              </Typography>
            </CenteredColumn>
          }
          onClose={dismiss}
          onConfirm={onDeleteConfirm}
        />
      );
    }
    const validUntil = moment(selectedMapping.validUntil);
    return (
      <GenericDialog
        open
        type={'confirmation'}
        title={'Update valid-until'}
        text={
          <Stack spacing={2} sx={{ minWidth: 300, pt: 1 }}>
            <Stack direction={'row'} spacing={2} alignItems={'center'}>
              <UserAvatar username={selectedMapping.user?.username ?? ''} size={'large'} />
              <Stack spacing={0.5}>
                <Typography fontWeight={600}>{selectedMapping.user?.username}</Typography>
                <Typography variant={'body2'} color={'text.secondary'}>
                  {selectedMapping.role?.name} · {selectedMapping.node?.coordinate}
                </Typography>
              </Stack>
            </Stack>
            <DatePicker
              label={'Valid-Until'}
              disablePast
              value={validUntil}
              onChange={onChangeValidUntil}
              minDate={moment().add(1, 'd')}
              sx={{ width: '100%' }}
            />
          </Stack>
        }
        onClose={dismiss}
        onConfirm={onValidUntilConfirm}
      />
    );
  }, [dialog, selectedMapping, onDeleteConfirm, onValidUntilConfirm, onChangeValidUntil]);

  if (isFetchingRole || !role) {
    return <LoadingMask />;
  }

  return (
    <SettingsSection
      title={`Users with role: ${role.name}`}
      description={role.coordinate ?? undefined}
      action={
        <Button
          size="small"
          color="inherit"
          startIcon={<ArrowBack fontSize="small" />}
          onClick={() => navigate('/me/admin/roles')}
        >
          Back to Roles
        </Button>
      }
      drawer={<UserMappingDrawer role={role} mapping={selectedMapping} />}
    >
      <AdminTable<UserRoleNodeMapping>
        fetchItems={fetchItems}
        columns={columns}
        primaryAction={
          canWrite ? (
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Add mapping
            </Button>
          ) : undefined
        }
      />
      {dialogElement}
    </SettingsSection>
  );
};
